
import React, { useEffect, useState } from 'react';
import { getUserCampaigns } from '../utils/firebase';
import { deleteCampaignSimple } from '../utils/firebaseSimple';
import { CampaignFormData } from '../types';
import { Users, Calendar, Building, AlertTriangle, Trash } from 'lucide-react';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from './ui/alert-dialog';
import { toast } from 'react-hot-toast';

interface CampaignListProps {
  userId: string;
  onSelectCampaign: (campaign: CampaignFormData) => void;
}

interface Campaign extends CampaignFormData {
  id: string;
  createdAt: string;
}

const CampaignList: React.FC<CampaignListProps> = ({ userId, onSelectCampaign }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsIndex, setNeedsIndex] = useState(false);
  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(null);

  const loadCampaigns = async () => {
    try {
      console.log('Loading campaigns for user:', userId);
      const userCampaigns = await getUserCampaigns(userId);
      console.log('Campaigns loaded successfully:', userCampaigns);
      setCampaigns(userCampaigns as Campaign[]);
      setError('');
      setNeedsIndex(false);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      
      // Check if it's the Firebase index error
      if (err instanceof Error && err.message.includes('query requires an index')) {
        setNeedsIndex(true);
        setError('Firebase index configuration needed. Please check the console for the index creation link.');
      } else {
        setError('Failed to load campaigns');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadCampaigns();
    }
  }, [userId]);

  const handleDeleteCampaign = async (campaignId: string, campaignName: string) => {
    try {
      setDeletingCampaignId(campaignId);
      console.log('Deleting campaign:', campaignId);
      
      await deleteCampaignSimple(campaignId);
      
      toast.success(`Campaign "${campaignName}" deleted successfully`);
      
      // Refresh the campaigns list
      await loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      
      // Show specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Permission denied')) {
          toast.error('Permission denied: You can only delete your own campaigns. Please check that you are logged in correctly.');
        } else if (error.message.includes('Authentication required')) {
          toast.error('Please log in again to delete campaigns.');
        } else {
          toast.error(`Failed to delete campaign: ${error.message}`);
        }
      } else {
        toast.error('Failed to delete campaign. Please try again.');
      }
    } finally {
      setDeletingCampaignId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (needsIndex) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Firebase Index Required</h3>
            <p className="text-yellow-700 mb-4">
              A Firebase composite index is required to load your campaigns. Please check the browser console for the index creation link, 
              or create the index manually in the Firebase Console.
            </p>
            <p className="text-sm text-yellow-600">
              Index needed: campaigns collection with fields: userId (Ascending), createdAt (Descending)
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-600">
        <AlertTriangle className="h-8 w-8 mx-auto mb-4" />
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="border-2 border-red-600 inline-block p-4 mb-4">
          <h2 className="text-2xl font-bold text-red-700 tracking-wider">
            YOUR POLITICAL
            <br />
            CAMPAIGNS
          </h2>
        </div>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-neutral-500 text-lg mb-4">
            No campaigns yet. Start by creating a new political campaign.
          </p>
          <div className="inline-flex items-center text-red-600">
            <Users className="h-5 w-5 mr-2" />
            <span className="font-medium">Ready to build your strategy?</span>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="p-6 rounded-lg border-2 border-neutral-200 hover:border-red-500 hover:shadow-lg transition-all duration-200 bg-white relative"
            >
              <div className="absolute top-4 right-4">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={deletingCampaignId === campaign.id}
                    >
                      {deletingCampaignId === campaign.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash className="h-4 w-4" />
                      )}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{campaign.core_issue_details.campaign_name}"? 
                        This action cannot be undone and will permanently remove all campaign data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteCampaign(campaign.id, campaign.core_issue_details.campaign_name)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete Campaign
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              <button
                onClick={() => onSelectCampaign(campaign)}
                className="text-left w-full pr-12"
              >
                <h3 className="font-bold text-lg text-red-700 mb-3">
                  {campaign.core_issue_details.campaign_name}
                </h3>
                
                <div className="space-y-2 text-sm text-neutral-600 mb-4">
                  <div className="flex items-center">
                    <Building className="h-4 w-4 mr-2 text-red-500" />
                    {campaign.core_issue_details.target_entity}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-red-500" />
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                    {campaign.core_issue_details.political_issue}
                  </span>
                  <span className="px-3 py-1 bg-neutral-100 text-neutral-700 text-xs rounded-full font-medium">
                    {campaign.core_issue_details.campaign_type}
                  </span>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignList;
